import { useTranslation } from 'react-i18next';
import { useDocumentsForm } from '../hooks/useDocumentsForm';
import Select from 'react-select';
import LocationDropDowns from '@shared/components/LocationDropDowns';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig';
import { majorContractsService } from '@shared/services/majorContracts';
import {
    clearDocumentsDraft,
    clearPersonFormReturnContext,
    loadDocumentsDraft,
    saveDocumentsDraft,
    savePersonFormReturnContext,
} from '@utils/personFormReturnStorage';

export default function DocumentsForm({ person, onClose, fetchData, showRegisterContextButtons = false }) {
    const { t } = useTranslation();
    const {
        register,
        errors,
        setValue,
        reset,
        getValues,
        photoUrl,
        handleImagePreview,
        personData,
        nationality,
        countryOptions,
        familyStatus,
        familyStatusOptions,
        defaultImagePath,
        handleFamilyStatusChange,
        handleNationalityChange,
        generateRandomId,
        onSubmit,
        watch,
        documentsResponse,
    } = useDocumentsForm(person, t, 'documents', fetchData, handleClose);
    const navigate = useNavigate();

    const [showWorkingCapacityButton, setShowWorkingCapacityButton] = useState(person?.invalid || false);
    const [showPensionHistoryButton, setShowPensionHistoryButton] = useState(person?.pensioner || false);
    const draftAppliedRef = useRef(false);

    useEffect(() => {
        const invalidValue = personData?.invalid ?? person?.invalid ?? false;
        setShowWorkingCapacityButton(Boolean(invalidValue));
        setValue('invalid', invalidValue);
    }, [personData, person, setValue]);

    useEffect(() => {
        const pensionerValue = personData?.pensioner ?? person?.pensioner ?? false;
        setShowPensionHistoryButton(Boolean(pensionerValue));
    }, [personData, person]);

    const resolvedPerson = personData || person || {};
    const personId = resolvedPerson.id ?? person?.id;

    const handleSendSecondEmail = async () => {
        if (!personId) {
            notifyError(t('common.errorMessage'));
            return;
        }

        try {
            await majorContractsService.sendSecondEmail(personId);
            notifySuccess('Успешно изпратен втори имейл');
        } catch (error) {
            console.error('Error sending second email:', error);
            notifyError(error?.response?.data?.message || error?.response?.data || t('common.errorMessage'));
        }
    };

    function handleClose() {
        clearDocumentsDraft(personId);
        clearPersonFormReturnContext();
        onClose?.();
    }

    // Restore draft once, after hook has fetched/reset defaults.
    useEffect(() => {
        if (!personId) return;
        if (draftAppliedRef.current) return;

        const parsed = loadDocumentsDraft(personId);
        if (!parsed) return;
        reset(parsed.values);
        draftAppliedRef.current = true;
    }, [personId, documentsResponse, reset]);

    const headerName = [
        resolvedPerson.name ?? person?.name,
        resolvedPerson.fathersName ?? person?.fathersName,
        resolvedPerson.surname ?? person?.surname
    ].filter(Boolean).join(' ');

    const countryValue = watch('country');
    const regionValue = watch('region');
    const subRegionValue = watch('subregion');
    const cityValue = watch('addressCityId');
    const addressValue = watch('address');

    useEffect(() => {
        setValue('postAddressCountryId', countryValue || 'BG');
    }, [countryValue, setValue]);

    useEffect(() => {
        setValue('postAddressRegionId', regionValue || null);
    }, [regionValue, setValue]);

    useEffect(() => {
        setValue('postAddressSubRegionId', subRegionValue || null);
    }, [subRegionValue, setValue]);

    useEffect(() => {
        setValue('postAddressCityId', cityValue || null);
    }, [cityValue, setValue]);

    useEffect(() => {
        setValue('postAddress', addressValue || null);
    }, [addressValue, setValue]);

    const locationData = useMemo(() => {
        if (!personData) return null;

        const countryId = personData.postAddressCountryId ?? 'BG';
        const regionId = personData.postAddressRegionId ?? null;
        const subregionId = personData.postAddressSubRegionId ?? null;
        const cityId = personData.postAddressCityId ?? null;

        if (!countryId && !regionId && !subregionId && !cityId) return null;

        return {
            addressCityId: {
                id: cityId,
                countryId,
                regionId,
                subregionId
            }
        };
    }, [personData]);

    const locationFormValues = useMemo(() => ({
        country: countryValue || null,
        region: regionValue || null,
        subregion: subRegionValue || null,
        city: cityValue || null
    }), [countryValue, regionValue, subRegionValue, cityValue]);

    return (
        <div className="documents-form-container">
            <h3 className="form-header">
                {t('persons.documents.title', {
                    name: headerName
                })}
            </h3>

            <form onSubmit={onSubmit}>
                <div className="form-content">
                    <div className="form-column">
                        <div className="form-row">
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.documents.name')}<span className="required">*</span></span>
                                <div className="form-input-fields">
                                    <input
                                        type="text"
                                        className={`form-control text-uppercase ${errors.foreignName ? 'is-invalid' : ''}`}
                                        placeholder={t('persons.documents.name')}
                                        {...register('foreignName')}
                                    />
                                    {errors.foreignName && (
                                        <div className="invalid-feedback">{errors.foreignName.message}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.documents.fathersName')}</span>
                                <div className="form-input-fields">
                                    <input
                                        type="text"
                                        className={`form-control text-uppercase ${errors.foreignFathersName ? 'is-invalid' : ''}`}
                                        placeholder={t('persons.documents.fathersName')}
                                        {...register('foreignFathersName')}
                                    />
                                    {errors.foreignFathersName && (
                                        <div className="invalid-feedback">{errors.foreignFathersName.message}</div>
                                    )}
                                </div>
                            </div>
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.documents.surname')}<span className="required">*</span></span>
                                <div className="form-input-fields">
                                    <input
                                        type="text"
                                        className={`form-control text-uppercase ${errors.foreignSurname ? 'is-invalid' : ''}`}
                                        placeholder={t('persons.documents.surname')}
                                        {...register('foreignSurname')}
                                    />
                                    {errors.foreignSurname && (
                                        <div className="invalid-feedback">{errors.foreignSurname.message}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.documents.familyStatus')}</span>
                                <Select
                                    className='select-container mb-2'
                                    name='familyStatus'
                                    placeholder={t('persons.documents.familyStatus')}
                                    options={familyStatusOptions}
                                    onChange={handleFamilyStatusChange}
                                    value={familyStatusOptions.find(option => option.value === familyStatus) || null}
                                />
                            </div>
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.documents.nationality')}</span>
                                <Select
                                    className="mb-2 select-container"
                                    name='nationality'
                                    placeholder={t('persons.documents.nationality')}
                                    options={countryOptions}
                                    onChange={handleNationalityChange}
                                    value={countryOptions.find(option => option.label === nationality) || null}
                                />
                            </div>
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.documents.randomId')}</span>
                                <input
                                    type="text"
                                    className="form-control"
                                    defaultValue={resolvedPerson.idBdsk || ''}
                                    readOnly
                                    {...register('idBdsk')}
                                />
                                <button type="button" className="form-btn btn-green" onClick={generateRandomId}>
                                    {t('persons.documents.generateId')}
                                </button>
                                {/* Keep it commented for now , until production ready
                                <button type="button" className="form-btn btn-green" onClick={handleSendSecondEmail}>
                                    {t('persons.documents.generateEmail')}
                                </button> */}
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="input-checkbox-wrapper d-inline-block">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="checkbox1"
                                    {...register('active')}
                                />
                                <label className="form-check-label" htmlFor="checkbox1">
                                    {t('persons.documents.isActive')}
                                </label>
                            </div>

                            <div className="input-checkbox-wrapper d-inline-block">
                                <div>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="checkbox2"
                                        {...register('pensioner', {
                                            onChange: (e) => setShowPensionHistoryButton(e.target.checked)
                                        })}
                                    />
                                    <label className="form-check-label" htmlFor="checkbox2">
                                        {t('persons.documents.pensioner')}
                                    </label>
                                </div>
                                {showPensionHistoryButton && (
                                    <div className="mt-1">
                                        <button
                                            type="button"
                                            className="form-btn btn-green"
                                            onClick={() => navigate(`/pension-history/${personId}`, { state: { person: resolvedPerson } })}
                                        >
                                            {t('Compensations as per labor contracts.pensionHistory.buttonTitle')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="input-checkbox-wrapper d-inline-block">
                                <div>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="checkbox3"
                                        defaultChecked={person?.invalid || false}
                                        {...register('invalid', {
                                            onChange: (e) => setShowWorkingCapacityButton(e.target.checked)
                                        })}
                                    />
                                    <label className="form-check-label" htmlFor="checkbox3">
                                        {t('persons.documents.isInvalid')}
                                    </label>
                                </div>
                                {showWorkingCapacityButton && (
                                    <div className="mt-1">
                                        <button
                                            type="button"
                                            className="form-btn btn-green"
                                            onClick={() => navigate(`/working-capacity/${personId}`, { state: { person: resolvedPerson } })}
                                        >
                                            {t('persons.workingCapacity.buttonTitle')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="picture-column">
                        <div className="picture-preview">
                            <img
                                src={photoUrl}
                                alt={t('persons.documents.preview')}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = defaultImagePath;
                                    setValue('personPhoto', defaultImagePath);
                                }}
                            />
                        </div>
                        <button
                            type="button" form
                            className="form-btn btn-green"
                            onClick={() => document.getElementById('personPhotoInput').click()}
                        >
                            {t('persons.documents.uploadPicture')}
                        </button>
                        <input
                            type="file"
                            className="form-control"
                            onChange={handleImagePreview}
                            accept="image/*"
                            name="personPhoto"
                            style={{ display: 'none' }}
                            id="personPhotoInput"
                        />
                    </div>
                </div>

                <div className="form-footer">
                    <div className='form-column'>
                        <LocationDropDowns
                            register={register}
                            setValue={setValue}
                            data={locationData}
                            formValues={locationFormValues}
                        />
                        <input type="hidden" {...register('postAddressCountryId')} />
                        <input type="hidden" {...register('postAddressRegionId')} />
                        <input type="hidden" {...register('postAddressSubRegionId')} />
                        <input type="hidden" {...register('postAddressCityId')} />
                        <input type="hidden" {...register('postAddress')} />
                        <input
                            type="text"
                            className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                            placeholder={t('persons.form.streetNumber')}
                            {...register('address')}
                        />
                        {errors.address && (
                            <div className="invalid-feedback">{errors.address.message}</div>
                        )}
                    </div>
                </div>
                {showRegisterContextButtons && (
                    <div className="form-footer">
                        <button
                            type="button"
                            className="form-btn btn-green"
                            onClick={() => {
                                const typeActivity = 0;
                                const values = getValues();
                                const returnPerson = {
                                    ...resolvedPerson,
                                    // Preserve register metadata required by other form fetches.
                                    recordId: person?.recordId ?? resolvedPerson?.recordId,
                                    empId: person?.empId ?? resolvedPerson?.empId,
                                    fullName: person?.fullName ?? resolvedPerson?.fullName,
                                    personId:
                                        person?.personId ??
                                        resolvedPerson?.personId ??
                                        person?.id ??
                                        resolvedPerson?.id,
                                };
                                saveDocumentsDraft(personId, values);
                                savePersonFormReturnContext({
                                    returnPath: '/personnel/employment-contracts/register',
                                    person: returnPerson,
                                    selectedRecord: {
                                        id: returnPerson.recordId,
                                        empId: returnPerson.empId,
                                        fullName: returnPerson.fullName,
                                        personId: returnPerson.personId,
                                    },
                                    activeForm: 'documents',
                                    showRegisterIcons: true,
                                    typeActivity,
                                });

                                // New screen should not be under the modal.
                                onClose?.();
                                navigate('/investment-services', { state: { person: returnPerson, typeActivity } });
                            }}
                        >
                            {t('persons.documents.investmentBroker')}
                        </button>
                        <button
                            type="button"
                            className="form-btn btn-green"
                            onClick={() => {
                                const typeActivity = 1;
                                const values = getValues();
                                const returnPerson = {
                                    ...resolvedPerson,
                                    recordId: person?.recordId ?? resolvedPerson?.recordId,
                                    empId: person?.empId ?? resolvedPerson?.empId,
                                    fullName: person?.fullName ?? resolvedPerson?.fullName,
                                    personId:
                                        person?.personId ??
                                        resolvedPerson?.personId ??
                                        person?.id ??
                                        resolvedPerson?.id,
                                };
                                saveDocumentsDraft(personId, values);
                                savePersonFormReturnContext({
                                    returnPath: '/personnel/employment-contracts/register',
                                    person: returnPerson,
                                    selectedRecord: {
                                        id: returnPerson.recordId,
                                        empId: returnPerson.empId,
                                        fullName: returnPerson.fullName,
                                        personId: returnPerson.personId,
                                    },
                                    activeForm: 'documents',
                                    showRegisterIcons: true,
                                    typeActivity,
                                });

                                onClose?.();
                                navigate('/investment-services', { state: { person: returnPerson, typeActivity } });
                            }}
                        >
                            {t('persons.documents.socialSecurityBroker')}
                        </button>
                    </div>
                )}
                <div className="form-footer">
                    <button type="submit" className="form-btn btn-blue">
                        {t('common.save')}
                    </button>
                    <button type="button" className="form-btn btn-red" onClick={handleClose}>
                        {t('common.cancel')}
                    </button>
                </div>
            </form>
        </div>
    );
} 